

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/Auth';
import {
  filterSearchResults,
  getResultText,
  getTypeLabel,
  searchResultPath,
} from 'utils/globalSearch';
import { useTravelUi } from 'hooks/useTravelUi';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Popper,
  Fade,
  Button,
  Divider,
  Icon,
  IconButton,
  Tooltip
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useAiAssistant } from 'context/AiAssistantContext';
import colors from 'styles/tokens/colors';
import { useTheme } from '@mui/material/styles';
import MDInput from "components/MDInput";
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ContactsIcon from '@mui/icons-material/Contacts';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CallButton from 'components/Softphone/CallButton';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EventIcon from '@mui/icons-material/Event';
import LuggageIcon from '@mui/icons-material/Luggage';
import PaymentIcon from '@mui/icons-material/Payment';
import GroupsIcon from '@mui/icons-material/Groups';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';


const SearchComponent = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const [allResults, setAllResults] = useState([]);
  const MAX_DROPDOWN_RESULTS = 5; // Number of results to show in dropdown

  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  // Travel hits (trips, travel orders, passengers...) only for tenants with
  // the TRAVEL edition and the company travel toggle on.
  const { showTravel } = useTravelUi();
  const aiAssistant = useAiAssistant();


  // Update the useEffect for search to handle multi-word queries
useEffect(() => {
  const delayDebounceFn = setTimeout(() => {
    // Check if search query has at least 2 characters after trimming
    if (searchQuery.trim().length >= 2) {
      performSearch();
    } else {
      setSearchResults([]);
      setOpen(false);
    }
  }, 300);

  return () => clearTimeout(delayDebounceFn);
}, [searchQuery, showTravel]);

   const performSearch = async () => {
    setLoading(true);
    try {
      const encodedQuery = encodeURIComponent(searchQuery.trim());
      const response = await axiosInstance.get(`/search?query=${encodedQuery}`);
      
      const results = filterSearchResults(response.data, showTravel);

      // Store all results
      setAllResults(results);

      // Show only limited results in dropdown
      setSearchResults(results.slice(0, MAX_DROPDOWN_RESULTS));
      setOpen(true);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      setAllResults([]);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };


  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && searchQuery.length >= 2) {
      event.preventDefault(); // Prevent form submission if inside a form
      performSearch(); // Trigger search immediately on Enter
    }
  };

  const handleResultClick = (result) => {
    const path = searchResultPath(result);
    if (path) navigate(path);
    setOpen(false);
    setSearchQuery('');
  };

  const handleViewAll = () => {
    navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
    setOpen(false);
    setSearchQuery('');
  };

  const getIcon = (type) => {
    switch (type) {
      case 'leads':
        return <BusinessIcon color="primary" sx={{ mr: 1 }} />;
      case 'users':
        return <PersonIcon color="info" sx={{ mr: 1 }} />;
      case 'deals':
        return <HandshakeIcon color="success" sx={{ mr: 1 }} />;
      case 'products':
        return <ShoppingBasketIcon color="warning" sx={{ mr: 1 }} />;
      case 'accounts':
        return <AccountBalanceIcon color="secondary" sx={{ mr: 1 }} />;
      case 'contacts':
        return <ContactsIcon color="error" sx={{ mr: 1 }} />;
      case 'orders':
        return <ShoppingCartIcon color="error" sx={{ mr: 1 }} />;
      case 'invoices':
        return <ContactsIcon color="success" sx={{ mr: 1 }} />;
      case 'quotation':
        return <ContactsIcon color="error" sx={{ mr: 1 }} />;
      case 'case':
      case 'cases':
        return <SupportAgentIcon color="error" sx={{ mr: 1 }} />;
      case 'trips':
        return <FlightTakeoffIcon color="info" sx={{ mr: 1 }} />;
      case 'trip_packages':
        return <Inventory2Icon color="primary" sx={{ mr: 1 }} />;
      case 'travel_orders':
        return <LuggageIcon color="success" sx={{ mr: 1 }} />;
      case 'package_components':
        return <MiscellaneousServicesIcon color="warning" sx={{ mr: 1 }} />;
      case 'departure_dates':
        return <EventIcon color="secondary" sx={{ mr: 1 }} />;
      case 'payment_templates':
        return <PaymentIcon color="info" sx={{ mr: 1 }} />;
      case 'travel_order_pax':
        return <GroupsIcon color="error" sx={{ mr: 1 }} />;
      default:
        return null;
    }
  };

/**
 * The number to dial for a quick-search hit, and the record to file the call
 * against.
 *
 * Only the three types that carry a phone number are dialable. Everything else
 * returns null, and CallButton renders nothing -- so a product or a quotation
 * row looks exactly as it did before.
 */
const dialableNumber = (result) => {
  switch (result.type) {
    case 'contacts':
      return result.mobile_number || result.phone || null;
    case 'leads':
    case 'accounts':
      return result.mobile || result.phone || null;
    default:
      return null;
  }
};

const callContext = (result) => {
  switch (result.type) {
    case 'contacts':
      return {
        parentTable: 'contacts',
        parentId: result.id,
        contactId: result.id,
        accountId: result.account_id,
      };
    case 'leads':
      return { parentTable: 'leads', parentId: result.id, leadId: result.id };
    case 'accounts':
      return { parentTable: 'accounts', parentId: result.id, accountId: result.id };
    default:
      return {};
  }
};




return (
  <Box ref={searchRef} sx={{ position: 'relative', width: '100%' }}>
    <MDInput
      fullWidth
      label="Search"
      value={searchQuery}
      onChange={handleSearchChange}
      onKeyDown={handleKeyDown}
      InputProps={{
        endAdornment: (loading || aiAssistant.enabled) ? (
          <>
            {loading && <CircularProgress size={20} />}
            {/* Only for users who may use it: licensed, seated, credits. */}
            {aiAssistant.enabled && (
              <Tooltip title="AI Assistant">
                <IconButton
                  size="small"
                  aria-label="Open the AI Assistant"
                  onClick={aiAssistant.toggle}
                  sx={{ ml: 0.5, color: aiAssistant.open ? colors.brand.primary : theme.palette.text.secondary }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : null,
        sx: {
          '&::placeholder': {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontWeight: 400,
          }
        }
      }}
      sx={{
        '& .MuiInputBase-root': {
          borderRadius: '10px',
          backgroundColor: isDark ? 'rgba(26, 44, 73, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          color: theme.palette.text.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(26, 44, 73, 1)' : 'rgba(255, 255, 255, 1)',
            boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
          },
          '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
          },
        },
      }}
    />

<Popper
        open={open && searchResults.length > 0}
        anchorEl={searchRef.current}
        transition
        placement="bottom-start"
        style={{ width: searchRef.current?.offsetWidth, zIndex: 1301 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper 
              elevation={3} 
              sx={{ 
                mt: 1, 
                maxHeight: '400px', 
                overflow: 'auto',
                borderRadius: '12px',
                boxShadow: isDark ? 'none' : '0 4px 20px rgba(0,0,0,0.1)',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                position: 'relative',
              }}
            >
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <List sx={{ py: 0 }}>
                  {/* Sticky View All button with updated styling */}
                  <ListItem
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        py: 1,  // Reduced padding
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'background.paper',
                        backdropFilter: 'blur(8px)',
                        justifyContent: 'center',
                      }}
                    >
                      <Button
                        onClick={handleViewAll}
                        size="small"
                        color="info"
                        variant="contained"
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          letterSpacing: '0.25px',
                          boxShadow: 'none',
                          background: (theme) => `linear-gradient(45deg, 
                            ${theme.palette.info.main} 30%, 
                            ${theme.palette.primary.main} 90%
                          )`,
                          fontFamily: '"Poppins", "Roboto", sans-serif',
                          fontSize: '0.813rem',
                          py: 0.5,  // Reduced padding
                          px: 2,    // Reduced padding
                          minWidth: '120px', // Set specific width
                          maxWidth: '150px', // Maximum width
                          '&:hover': {
                            boxShadow: '0 2px 8px rgba(0, 158, 219, 0.3)',
                            transform: 'translateY(-1px)',
                            background: (theme) => `linear-gradient(45deg, 
                              ${theme.palette.info.dark} 30%, 
                              ${theme.palette.primary.dark} 90%
                            )`,
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          }
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.813rem',
                            color: '#ffffff',
                            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                          }}
                        >
                          View All
                        </Typography>
                      </Button>
                    </ListItem>

                  {/* Results list with divider after sticky header */}
                  <Box sx={{ 
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 16,
                      right: 16,
                      height: '1px',
                      backgroundColor: 'divider',
                    }
                  }}>
                    {searchResults.slice(0, 5).map((result, index) => (
                      <ListItem
                        key={`${result.type}-${result.id}`}
                        button
                        onClick={() => handleResultClick(result)}
                        divider={index < Math.min(searchResults.length, 5) - 1}
                        sx={{
                          py: 1.5,
                          px: 2,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isDark
                              ? 'rgba(168, 200, 255, 0.08)'
                              : 'rgba(0, 158, 219, 0.05)',
                            transform: 'translateX(4px)'
                          },
                        }}
                      >
                        {getIcon(result.type)}
                        <ListItemText
                          primary={
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                color: 'text.main',
                              }}
                            >
                              {getResultText(result)}
                            </Typography>
                          }
                          secondary={
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 400,
                                fontSize: '0.75rem'
                              }}
                            >
                              {getTypeLabel(result.type)}
                            </Typography>
                          }
                          sx={{ my: 0 }}
                        />
                        {/* The row itself navigates, so the call must not
                            bubble up into that -- an agent reaching for the
                            phone does not want the record page as well. */}
                        <Box
                          onClick={(e) => e.stopPropagation()}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <CallButton
                            phone={dialableNumber(result)}
                            {...callContext(result)}
                            variant="icon"
                            size="small"
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </Box>
                </List>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
  </Box>
);
};

export default SearchComponent;